import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as customersApi from './customers.api';

import type { DocumentUploadMeta } from './customers.api';
import type {
  CreateCustomerRequest,
  CustomerAddressInput,
  CustomerContactInput,
  CustomerListQuery,
  UpdateCustomerRequest,
} from '@utazzo/shared';

import { useCountries, useStates } from '@/features/cities/use-cities';

const QK = {
  list: (query: CustomerListQuery) => ['customers', 'list', query] as const,
  detail: (id: string) => ['customers', 'detail', id] as const,
  branches: ['customers', 'branch-options'] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const useCustomersList = (query: CustomerListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => customersApi.listCustomers(query),
    staleTime: 30_000,
  });

export const useCustomer = (id: string | null) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => customersApi.getCustomer(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

export const useBranchOptions = () =>
  useQuery({
    queryKey: QK.branches,
    queryFn: customersApi.listBranchOptions,
    // Branch list is stable within a session.
    staleTime: 5 * 60_000,
  });

// Re-export geography hooks so step components don't reach across feature
// boundaries directly.
export { useCountries, useStates };

// ---------------------------------------------------------------------------
// Mutations - customer core
// ---------------------------------------------------------------------------

export const useCreateCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCustomerRequest) => customersApi.createCustomer(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', 'list'] });
      toast.success('Customer created');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create customer'),
  });
};

/**
 * 2026-07-09. `suppressSuccessToast` matches the pattern on
 * `useUpdateLead` / `useTransitionLeadStage` - callers chaining this
 * mutation with a follow-up step (a lead PATCH + a stage transition
 * from the lead edit form, for example) can silence the standalone
 * "Customer updated" toast so the operator sees ONE final outcome
 * message instead of overlapping toasts.
 */
interface UpdateCustomerVariables {
  id: string;
  payload: UpdateCustomerRequest;
  suppressSuccessToast?: boolean;
}

export const useUpdateCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: UpdateCustomerVariables) =>
      customersApi.updateCustomer(id, payload),
    onSuccess: (customer, variables) => {
      qc.invalidateQueries({ queryKey: ['customers', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(customer.id) });
      if (!variables.suppressSuccessToast) {
        toast.success('Customer updated');
      }
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update customer'),
  });
};

export const useDeleteCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customersApi.deleteCustomer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', 'list'] });
      toast.success('Customer deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete customer'),
  });
};

export const useBulkSetCustomersStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      customersApi.bulkSetCustomersStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update customer status'),
  });
};

// ---------------------------------------------------------------------------
// Mutations - addresses
// ---------------------------------------------------------------------------

export const useAddAddress = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, payload }: { customerId: string; payload: CustomerAddressInput }) =>
      customersApi.addAddress(customerId, payload),
    onSuccess: (customer) => {
      qc.invalidateQueries({ queryKey: QK.detail(customer.id) });
      toast.success('Address added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add address'),
  });
};

export const useRemoveAddress = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, addressId }: { customerId: string; addressId: string }) =>
      customersApi.removeAddress(customerId, addressId),
    onSuccess: (customer) => {
      qc.invalidateQueries({ queryKey: QK.detail(customer.id) });
      toast.success('Address removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove address'),
  });
};

// ---------------------------------------------------------------------------
// Mutations - contacts
// ---------------------------------------------------------------------------

export const useAddContact = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, payload }: { customerId: string; payload: CustomerContactInput }) =>
      customersApi.addContact(customerId, payload),
    onSuccess: (customer) => {
      qc.invalidateQueries({ queryKey: QK.detail(customer.id) });
      toast.success('Contact added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add contact'),
  });
};

export const useRemoveContact = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, contactId }: { customerId: string; contactId: string }) =>
      customersApi.removeContact(customerId, contactId),
    onSuccess: (customer) => {
      qc.invalidateQueries({ queryKey: QK.detail(customer.id) });
      toast.success('Contact removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove contact'),
  });
};

// ---------------------------------------------------------------------------
// Mutations - documents
// ---------------------------------------------------------------------------

export const useUploadDocument = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerId,
      file,
      meta,
    }: {
      customerId: string;
      file: File;
      meta: DocumentUploadMeta;
    }) => customersApi.uploadCustomerDocument(customerId, file, meta),
    onSuccess: (_doc, variables) => {
      qc.invalidateQueries({ queryKey: QK.detail(variables.customerId) });
      toast.success('Document uploaded');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to upload document'),
  });
};

export const useDeleteDocument = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, documentId }: { customerId: string; documentId: string }) =>
      customersApi.deleteDocument(customerId, documentId),
    onSuccess: (customer) => {
      qc.invalidateQueries({ queryKey: QK.detail(customer.id) });
      toast.success('Document deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete document'),
  });
};

export const useDownloadDocument = () =>
  useMutation({
    mutationFn: ({ customerId, documentId }: { customerId: string; documentId: string }) =>
      customersApi.downloadDocument(customerId, documentId),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to generate download link'),
  });

// ---------------------------------------------------------------------------
// Shared error extractor (mirrors the pattern from other features)
// ---------------------------------------------------------------------------

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
